import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from '../config/axiosConfig'; 
import { FaEye, FaEyeSlash, FaLock, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import './ResetPassword.css';

const ResetPassword = () => {
  const [step, setStep] = useState(1); // 1: Verify token, 2: Reset password, 3: Success
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  
  // Password form
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });
  
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Extract token from URL query parameter
    const params = new URLSearchParams(location.search);
    const urlToken = params.get('token');
    
    if (urlToken) {
      setToken(urlToken);
      verifyToken(urlToken);
    }
  }, [location]);

  const verifyToken = async (tokenToVerify) => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.post('https://hsn.urugowoc.org.rw/backend/api/verify-reset-token', {
        token: tokenToVerify
      });

      if (response.data.valid) {
        setEmail(response.data.user.email);
        setStep(2); // Move to password reset form
        setMessage('');
      } else {
        setError(response.data.error || 'Invalid or expired reset token');
        setStep(1);
      }
    } catch (error) {
      console.error('Token verification error:', error);
      setError(error.response?.data?.error || 'Failed to verify reset token');
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);

    // Check password requirements
    const requirements = {
      length: newPassword.length >= 6,
      uppercase: /[A-Z]/.test(newPassword),
      lowercase: /[a-z]/.test(newPassword),
      number: /[0-9]/.test(newPassword),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword)
    };

    setPasswordRequirements(requirements);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password strength
    if (!passwordRequirements.length) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post('https://hsn.urugowoc.org.rw/backend/api/reset-password', {
        token,
        newPassword: password,
        confirmPassword
      });

      if (response.data.success) {
        setStep(3); // Show success message
        setMessage(response.data.message);
        
        // Auto-redirect to login after 5 seconds
        setTimeout(() => {
          navigate('/login');
        }, 5000);
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setError(error.response?.data?.error || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const renderStep = () => {
    switch(step) {
      case 1: // Verifying token
        return (
          <div className="reset-step">
            <div className="loading-spinner"></div>
            <p>Verifying reset link...</p>
            {error && (
              <div className="alert alert-error">
                {error}
                <div className="mt-3">
                  <Link to="/forgot-password" className="btn btn-secondary">
                    Request New Reset Link
                  </Link>
                </div>
              </div>
            )}
          </div>
        );

      case 2: // Reset password form
        return (
          <form className="reset-form" onSubmit={handleSubmit}>
            <div className="form-header">
              <h2>Reset Your Password</h2>
              <p>Enter a new password for your account: {email}</p>
            </div>

            {error && (
              <div className="alert alert-error">{error}</div>
            )}

            <div className="form-group">
              <label>New Password</label>
              <div className="password-input-wrapper">
                <FaLock className="form-icon" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Enter new password"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                  disabled={loading}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Confirm Password</label>
              <div className="password-input-wrapper">
                <FaLock className="form-icon" />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={toggleConfirmPasswordVisibility}
                  disabled={loading}
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            {/* Password requirements */}
            <div className="password-requirements">
              <h4>Password Requirements:</h4>
              <ul>
                <li className={passwordRequirements.length ? 'valid' : 'invalid'}>
                  {passwordRequirements.length ? <FaCheckCircle /> : <FaTimesCircle />}
                  At least 6 characters
                </li>
                <li className={passwordRequirements.uppercase ? 'valid' : 'invalid'}>
                  {passwordRequirements.uppercase ? <FaCheckCircle /> : <FaTimesCircle />}
                  At least one uppercase letter
                </li>
                <li className={passwordRequirements.lowercase ? 'valid' : 'invalid'}>
                  {passwordRequirements.lowercase ? <FaCheckCircle /> : <FaTimesCircle />}
                  At least one lowercase letter
                </li>
                <li className={passwordRequirements.number ? 'valid' : 'invalid'}>
                  {passwordRequirements.number ? <FaCheckCircle /> : <FaTimesCircle />}
                  At least one number
                </li>
                <li className={passwordRequirements.special ? 'valid' : 'invalid'}>
                  {passwordRequirements.special ? <FaCheckCircle /> : <FaTimesCircle />}
                  At least one special character
                </li>
              </ul>
            </div>

            <button 
              type="submit" 
              className="btn btn-primary btn-block"
              disabled={loading}
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </button>
          </form>
        );

      case 3: // Success
        return (
          <div className="reset-step success-step">
            <div className="success-icon">
              <FaCheckCircle />
            </div>
            <h2>Password Reset Successful!</h2>
            <div className="alert alert-success">
              {message || 'Your password has been reset successfully.'}
            </div>
            <p>You will be redirected to the login page in 5 seconds...</p>
            <div className="mt-4">
              <Link to="/" className="btn btn-primary">
                Go to Login Now
              </Link>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="reset-password-container">
      <div className="reset-password-card">
        <div className="card-header">
          <h1>Urugo Women Opportunity Center</h1>
          <p>Password Reset</p>
        </div>
        
        <div className="card-body">
          {renderStep()}
        </div>
        
        <div className="card-footer">
          <p>
            Remember your password?{' '}
            <Link to="/login">Sign in here</Link>
          </p>
          <p>
            Need an account?{' '}
            <Link to="/">Create account</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;